/**
 * 行为埋点 SDK - 核心追踪器
 * 唯一对外业务门面，业务代码只与此交互
 */
import type { AnalyticsConfig, AnalyticsEvent } from './types';
import { sanitizeProperties } from './sanitize';
import { enqueue, initTransport, flush } from './transport';
import { getSessionId, getUserContext, getRouteContext } from '../tracking-context';
import { generateUUID } from '@/lib/utils';

let trackerConfig: AnalyticsConfig | null = null;
let currentUserId: string | null = null;

// ─── 设备信息（初始化时一次性采集，避免重复解析）────────────────────

interface DeviceContext {
  browser: string;
  browser_version: string;
  os: string;
  screen_width: number;
  screen_height: number;
  language: string;
  timezone: string;
}

function resolveDeviceContext(): DeviceContext {
  const ua = navigator.userAgent;

  let browser = 'unknown';
  let browser_version = '';

  if (/Edg\//.test(ua)) {
    browser = 'Edge';
    browser_version = (ua.match(/Edg\/([\d.]+)/) ?? [])[1] ?? '';
  } else if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) {
    browser = 'Chrome';
    browser_version = (ua.match(/Chrome\/([\d.]+)/) ?? [])[1] ?? '';
  } else if (/Firefox\//.test(ua)) {
    browser = 'Firefox';
    browser_version = (ua.match(/Firefox\/([\d.]+)/) ?? [])[1] ?? '';
  } else if (/Safari\//.test(ua) && !/Chrome/.test(ua)) {
    browser = 'Safari';
    browser_version = (ua.match(/Version\/([\d.]+)/) ?? [])[1] ?? '';
  }

  let os = 'unknown';
  if (/Windows NT/.test(ua)) os = 'Windows';
  else if (/Mac OS X/.test(ua)) os = 'macOS';
  else if (/Linux/.test(ua)) os = 'Linux';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/iPhone|iPad|iPod/.test(ua)) os = 'iOS';

  return {
    browser,
    browser_version,
    os,
    screen_width: screen.width,
    screen_height: screen.height,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}

let deviceContext: DeviceContext | null = null;

/** 构造事件基础字段 */
function buildEvent(eventName: string, properties: Record<string, unknown>): AnalyticsEvent {
  const cfg = trackerConfig!;
  const routeCtx = getRouteContext();
  const userCtx = getUserContext();

  const mergedProps = sanitizeProperties({
    ...deviceContext,
    ...properties,
  });

  return {
    eventId: generateUUID(),
    occurredAt: new Date().toISOString(),
    eventName,
    properties: mergedProps,
    sessionId: getSessionId(),
    userId: currentUserId ?? userCtx?.userId ?? null,
    routeName: routeCtx.routeName,
    pathname: routeCtx.pathname,
    release: cfg.release,
    environment: cfg.environment,
  };
}

/** 开发模式日志 */
function devLog(eventName: string, properties: Record<string, unknown>): void {
  if (typeof console !== 'undefined') {
    console.debug('[Analytics DEV] tracked:', eventName, properties);
  }
}

// ─── 公开 API ────────────────────────────────────────────────────

/** 初始化 SDK */
function init(cfg: AnalyticsConfig): void {
  trackerConfig = {
    sampleRate: 1.0,
    flushBatchSize: 10,
    flushInterval: 5000,
    maxQueueSize: 200,
    ...cfg,
  };
  deviceContext = resolveDeviceContext();
  initTransport(trackerConfig);
}

/**
 * 手动追踪事件
 * @param eventName 事件名（全小写下划线）
 * @param properties 事件属性（可选）
 */
function track(eventName: string, properties: Record<string, unknown> = {}): void {
  if (!trackerConfig) return;

  if (!trackerConfig.enabled) {
    if (import.meta.env.DEV) devLog(eventName, properties);
    return;
  }

  if (import.meta.env.DEV) devLog(eventName, properties);

  const event = buildEvent(eventName, properties);
  enqueue(event);
}

/**
 * 自动追踪页面访问（路由切换时调用）
 */
function page(): void {
  const routeCtx = getRouteContext();
  track('page_view', {
    route_name: routeCtx.routeName,
    pathname: routeCtx.pathname,
  });
}

/**
 * 绑定/解绑用户身份
 * 登录后调用 identify(userId)，退出登录后调用 identify(null)
 */
function identify(userId: string | null): void {
  currentUserId = userId;
}

/** 判断 SDK 是否已启用 */
function isEnabled(): boolean {
  return trackerConfig?.enabled ?? false;
}

/** 手动 flush */
async function flushEvents(): Promise<void> {
  await flush();
}

export const analyticsTracker = {
  init,
  track,
  page,
  identify,
  isEnabled,
  flush: flushEvents,
};
